import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, LogOut } from "lucide-react";
import { onVersionChange, isUpdateAvailable, checkAppVersion, api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

// Non-dismissible notice shown to logged-in users when the admin publishes a new
// app version. We never force a logout, the user taps "Log Out Now" when ready,
// which signs them out and sends them to the login screen to pick up the latest build.
export default function VersionUpdateModal() {
  const { user, logout } = useAuth();
  const [show, setShow] = useState(isUpdateAvailable());
  const [busy, setBusy] = useState(false);

  // While the admin's deployment heads-up window is active, the dismissible
  // DeployNoticeBanner is the only prompt — suppress this modal so users aren't
  // double-prompted. Once the window closes, the normal re-login modal applies.
  const { data: notice } = useQuery({
    queryKey: ["deploy-notice"],
    queryFn: () => api("/deploy-notice"),
    enabled: !!user,
    refetchInterval: 10000,
  });
  const noticeActive = !!notice?.active;

  useEffect(() => {
    const unsub = onVersionChange(() => setShow(true));
    // Proactively re-check when the tab regains focus, covering idle sessions
    // that aren't making other API calls.
    const onFocus = () => checkAppVersion();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      unsub();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  const doLogout = async () => {
    setBusy(true);
    try { await logout(); } finally {
      window.location.assign("/login?updated=1");
    }
  };

  // Only prompt authenticated users; anonymous visitors already load fresh on next nav.
  if (!show || !user || noticeActive) return null;

  return (
    <div
      data-testid="version-update-modal"
      className="fixed inset-0 z-[400] flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-md bg-[#181427] text-white rounded-3xl shadow-2xl p-6 animate-rise">
        <div className="w-12 h-12 rounded-2xl bg-[#E8614A]/20 flex items-center justify-center mb-4">
          <RefreshCw className="w-6 h-6 text-[#E8614A]" />
        </div>
        <h2 className="font-serif text-2xl leading-snug mb-2">A new version of Two Degrees is available!</h2>
        <p className="text-white/70 text-sm leading-relaxed mb-6">
          Log out and back in to experience the latest updates.
        </p>
        <button
          data-testid="version-update-logout-btn"
          onClick={doLogout}
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#E8614A] hover:bg-[#d8543c] disabled:opacity-60 text-white text-sm font-semibold rounded-full px-5 py-3 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          {busy ? "Logging out…" : "Log Out Now"}
        </button>
      </div>
    </div>
  );
}
